import axios from 'axios';

// API base URL - in dev, use proxy with /api prefix, or explicit URL in prod
// The proxy in package.json forwards requests to backend
// Backend routes are registered with /api prefix
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

let authToken = null;
let accountId = null;

export const setAuthToken = (token) => {
  authToken = token;
  // Also store in localStorage for SSE access (EventSource doesn't support custom headers)
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

// Export function to check if token is set (for TimezoneProvider)
export const hasAuthToken = () => !!authToken;

// Export function to get token (for SSE)
export const getAuthToken = () => authToken || localStorage.getItem('authToken');

export const setAccountId = (id) => {
  accountId = id;
  // Also store in localStorage for SSE access
  if (id) {
    localStorage.setItem('accountId', id);
  } else {
    localStorage.removeItem('accountId');
  }
};

// Export function to get account ID (for SSE)
export const getAccountId = () => accountId || localStorage.getItem('accountId');

// Create axios instance
// When baseURL is '/api', axios uses relative URLs which work with the proxy
const api = axios.create({
  baseURL: API_BASE_URL, // '/api' in dev = uses proxy, set in prod = direct URL
  timeout: 120000, // 120 seconds timeout (increased from 30s to handle QuickNode rate limiting with retries)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    } else {
      console.warn(`[API] No auth token for ${config.method?.toUpperCase()} ${config.url}`);
    }
    if (accountId) {
      config.headers['x-account-id'] = accountId;
    }
    const accountIdInfo = accountId ? ` [accountId: ${accountId.slice(0, 8)}...]` : ' [NO ACCOUNT ID]';
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}${authToken ? ' (authenticated)' : ' (no auth)'}${accountIdInfo}`);
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      console.error('[API] Response error:', error.response.status, error.response.data);
      console.error('[API] Request URL:', error.config?.url);
      console.error('[API] Request headers:', error.config?.headers);
      
      // If 401 and we have a user, try to refresh token
      if (error.response.status === 401 && error.config && !error.config._retry) {
        error.config._retry = true;
        
        // Try to get fresh token from Firebase if available
        const { useAuth } = await import('../auth/AuthContext');
        // Note: We can't use hooks here, so we'll handle token refresh in the component
        console.warn('[API] 401 error - token may have expired or is invalid');
        console.warn('[API] Current auth token present:', !!authToken);
      }
    } else if (error.request) {
      console.error('[API] No response received:', error.message);
    } else {
      console.error('[API] Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// ============ Health ============
export const checkHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};

// ============ Balances ============
export const getBalances = async () => {
  const response = await api.get('/balances');
  return response.data;
};

// ============ Transfers ============
export const initiateTransfer = async (transferData) => {
  try {
    const response = await api.post('/transfers', transferData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTransferStatus = async (transferId) => {
  const response = await api.get(`/transfers/${transferId}`);
  return response.data;
};

export const cancelTransfer = async (transferId) => {
  const response = await api.post(`/transfers/${transferId}/cancel`);
  return response.data;
};

export const markTransferComplete = async (transferId) => {
  const response = await api.post(`/transfers/${transferId}/complete`);
  return response.data;
};

export const recheckTransfer = async (transferId) => {
  const response = await api.post(`/transfers/${transferId}/recheck`);
  return response.data;
};

export const updateTransferInitiatedBy = async (transferId, initiatedBy) => {
  const response = await api.patch(`/transfers/${transferId}/initiated-by`, { initiatedBy });
  return response.data;
};

export const updateTransferStatus = async (transferId, status, errorMessage = null) => {
  const response = await api.patch(`/transfers/${transferId}/status`, { status, errorMessage });
  return response.data;
};

export const getTransferHistory = async (params = {}) => {
  const response = await api.get('/transfers/history', { params });
  return response.data;
};

// ============ History ============
export const getDepositHistory = async (params = {}) => {
  const response = await api.get('/deposits/history', { params });
  return response.data;
};

export const getWithdrawalHistory = async (params = {}) => {
  const response = await api.get('/withdrawals/history', { params });
  return response.data;
};

export const getOnchainHistory = async (params = {}) => {
  const response = await api.get('/transfers/onchain/history', { params });
  return response.data;
};

export const getBlockchainHistory = async (params = {}) => {
  const response = await api.get('/blockchain/history', { params });
  return response.data;
};

export const getWalletTransactions = async (address, params = {}) => {
  const response = await api.get(`/wallet-transactions/${address}`, { params });
  return response.data;
};

// ============ Accounts ============
export const listAccounts = async () => {
  const response = await api.get('/accounts');
  return response.data;
};

export const createAccount = async (name) => {
  const response = await api.post('/accounts', { name });
  return response.data;
};

export const getAccount = async (accountId) => {
  const response = await api.get(`/accounts/${accountId}`);
  return response.data;
};

export const updateAccount = async (accountId, data) => {
  const response = await api.put(`/accounts/${accountId}`, data);
  return response.data;
};

export const deleteAccount = async (accountId) => {
  const response = await api.delete(`/accounts/${accountId}`);
  return response.data;
};

export const addAccountMember = async (accountId, email, role = 'user') => {
  const response = await api.post(`/accounts/${accountId}/members`, { email, role });
  return response.data;
};

export const updateAccountMember = async (accountId, memberId, role) => {
  const response = await api.put(`/accounts/${accountId}/members/${memberId}`, { role });
  return response.data;
};

export const removeAccountMember = async (accountId, memberId) => {
  const response = await api.delete(`/accounts/${accountId}/members/${memberId}`);
  return response.data;
};

export const bootstrapAccount = async () => {
  const response = await api.post('/accounts/bootstrap');
  return response.data;
};

// ============ API Keys ============
export const listApiKeys = async () => {
  const response = await api.get('/api-keys');
  return response.data;
};

export const createApiKey = async (payload) => {
  const response = await api.post('/api-keys', payload);
  return response.data;
};

export const revokeApiKey = async (id) => {
  const response = await api.delete(`/api-keys/${id}`);
  return response.data;
};

export const updateApiKey = async (id, payload) => {
  const response = await api.patch(`/api-keys/${id}`, payload);
  return response.data;
};

// ============ Exchange Secrets ============
export const listExchangeSecrets = async () => {
  const response = await api.get('/exchange-secrets');
  return response.data;
};

export const upsertExchangeSecret = async (payload) => {
  const response = await api.post('/exchange-secrets', payload);
  return response.data;
};

export const deleteExchangeSecret = async (exchange) => {
  const response = await api.delete(`/exchange-secrets/${exchange}`);
  return response.data;
};

// ============ Admin / Whitelist ============
export const getWhitelist = async () => {
  const response = await api.get('/admin/whitelist');
  return response.data;
};

export const updateWhitelist = async (whitelistData) => {
  const response = await api.post('/admin/whitelist', whitelistData);
  return response.data;
};

export const addWhitelistEntry = async (entryData) => {
  const response = await api.post('/admin/whitelist/entry', entryData);
  return response.data;
};

export const removeWhitelistEntry = async (type, identifier) => {
  const response = await api.delete(`/admin/whitelist/entry/${type}/${identifier}`);
  return response.data;
};

// ============ Exchange Configuration ============
export const getExchangeConfig = async () => {
  const response = await api.get('/admin/exchanges');
  return response.data;
};

export const updateExchangeConfig = async (exchange, configData) => {
  const response = await api.put(`/admin/exchanges/${exchange}/config`, configData);
  return response.data;
};

export const testExchangeConnection = async (exchange) => {
  const response = await api.post(`/admin/exchanges/${exchange}/test`);
  return response.data;
};

// ============ SWR Fetcher ============
// For use with SWR hooks
export const swrFetcher = async (url) => {
  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('SWR Fetcher error:', error);
    throw error;
  }
};

export default api;

